import type { SupplierManufacturingLocation } from '@tandemone/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createSupplierLocation,
  deleteSupplierLocation,
  getSupplierLocations,
  updateSupplierLocation
} from './api'
import { type FieldDef, ContactRowList } from './contact-row-list'
import { SUPPLIER_BUILDING_SIZE_UNIT_LABELS } from './enum-labels'
import { FormSkeleton } from './form-skeleton'
import { invalidateOnboarding } from './invalidate-onboarding'
import { PrimaryButton } from './primary-button'
import { StepFooter } from './step-footer'

const FIELDS: ReadonlyArray<FieldDef<SupplierManufacturingLocation>> = [
  {
    key: 'location',
    label: 'Address',
    type: 'address',
    required: true,
    placeholder: 'Start typing an address…',
    addressAutoFill: {
      country: 'country',
      region: 'region',
      latitude: 'latitude',
      longitude: 'longitude'
    }
  },
  {
    key: 'buildingSize',
    label: 'Building size',
    type: 'numberWithUnit',
    unitKey: 'buildingSizeUnit',
    unitOptions: [
      { value: 'sq_ft', label: SUPPLIER_BUILDING_SIZE_UNIT_LABELS.sq_ft },
      { value: 'sq_m', label: SUPPLIER_BUILDING_SIZE_UNIT_LABELS.sq_m }
    ]
  },
  {
    key: 'numberOfShifts',
    label: 'Number of shifts',
    type: 'number',
    min: 1
  },
  {
    key: 'workingHoursPerDay',
    label: 'Working hours per day',
    type: 'number',
    min: 1,
    max: 24
  }
]

const EMPTY_DRAFT: Omit<SupplierManufacturingLocation, 'id'> = {
  location: '',
  region: null,
  country: null,
  latitude: null,
  longitude: null,
  buildingSize: null,
  buildingSizeUnit: null,
  numberOfShifts: null,
  workingHoursPerDay: null
}

export function SupplierLocationsForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const queryClient = useQueryClient()
  const invalidate = () =>
    invalidateOnboarding(queryClient, 'supplier-locations')

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-locations'],
    queryFn: ({ signal }) => getSupplierLocations(signal)
  })

  const addMutation = useMutation({
    mutationFn: createSupplierLocation,
    onSuccess: invalidate
  })
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      patch
    }: {
      id: string
      patch: Partial<Omit<SupplierManufacturingLocation, 'id'>>
    }) => updateSupplierLocation(id, patch),
    onSuccess: invalidate
  })
  const removeMutation = useMutation({
    mutationFn: deleteSupplierLocation,
    onSuccess: invalidate
  })

  if (isLoading) return <FormSkeleton />
  const rows = data?.locations ?? []
  const canContinue = rows.length >= 1

  return (
    <div className='flex w-full flex-col gap-4'>
      <section className='rounded-2xl border border-border bg-page p-6'>
        <ContactRowList<SupplierManufacturingLocation>
          rows={rows}
          fields={FIELDS}
          emptyDraft={EMPTY_DRAFT}
          addLabel='Add another location'
          emptyState='Add at least one manufacturing location'
          onAdd={draft => addMutation.mutateAsync(draft)}
          onSave={async (id, patch) => {
            await updateMutation.mutateAsync({ id, patch })
          }}
          onRemove={async id => {
            await removeMutation.mutateAsync(id)
          }}
        />
      </section>

      <StepFooter
        hint={
          rows.length === 0
            ? 'Add at least one location'
            : `${rows.length} location${rows.length === 1 ? '' : 's'}`
        }
      >
        <PrimaryButton
          type='button'
          disabled={!canContinue}
          onClick={onSuccess}
        >
          {submitLabel}
        </PrimaryButton>
      </StepFooter>
    </div>
  )
}
